/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ElementPart, Part, nothing } from "lit"
import { directive } from "lit/directive.js"
import { AsyncDirective } from "lit/async-directive.js"

type EventListenerWithOptions = EventListenerOrEventListenerObject &
  Partial<AddEventListenerOptions>;

export class SpreadPropsDirective extends AsyncDirective {
  host!: EventTarget | object | Element;
  element!: Element;
  prevData: { [key: string]: unknown } = {};

  override render(_spreadData: { [key: string]: unknown }): unknown {
    return nothing;
  }

  override update(_part: Part, [spreadData]: Parameters<this["render"]>) {
    if (this.element !== (_part as ElementPart).element) {
      this.element = (_part as ElementPart).element;
    }
    this.host = _part.options?.host || this.element;
    this.apply(spreadData);
    this.groom(spreadData);
    this.prevData = { ...spreadData };
  }

  apply(data: { [key: string]: unknown }) {
    if (!data) { return }
    const { prevData, element } = this
    for (const key in data) {
      const value = data[key];
      if (value === prevData[key]) {
        continue;
      }
      // @ts-ignore
      element[key] = value
    }
  }

  groom(data: { [key: string]: unknown }) {
    const { prevData, element } = this
    if (!prevData) { return; }
    for (const key in prevData) {
      // @ts-ignore
      if (!data || (!(key in data) && element[key] === prevData[key])) {
        // @ts-ignore
        element[key] = undefined
      }
    }
  }
}

export const spreadProps = directive(SpreadPropsDirective);

export class SpreadEventsDirective extends SpreadPropsDirective {
  eventData: { [key: string]: unknown } = {};

  override apply(data: { [key: string]: unknown; }): void {
    if (!data) { return; }
    for (const key in data) {
      const value = data[key];
      if (value === this.eventData[key]) {
        continue;
      }
      this.applyEvent(key, value as EventListenerWithOptions);
    }
  }

  applyEvent(eventName: string, eventValue: EventListenerWithOptions) {
    const { prevData, element } = this
    this.eventData[eventName] = eventValue;
    const prevHandler = prevData[eventName];
    if (prevHandler) {
      element.removeEventListener(eventName, this, eventValue);
    }
    element.addEventListener(eventName, this, eventValue);
  }

  override groom(data: { [key: string]: unknown; }): void {
    const { prevData, element } = this
    if (!prevData) { return; }
    for (const key in prevData) {
      // @ts-ignore
      if (!data || (!(key in data) && element[key] === prevData[key])) {
        this.groomEvent(key, prevData[key] as EventListenerWithOptions);
      }
    }
  }

  groomEvent(eventName: string, eventValue: EventListenerWithOptions) {
    const { element } = this
    delete this.eventData[eventName];
    element.removeEventListener(eventName, this, eventValue);
  }

  handleEvent(event: Event) {
    const value: ((event: Event) => void) | EventListenerObject = this.eventData[event.type] as ((event: Event) => void) | EventListenerObject;
    if (typeof value === 'function') {
      value.call(this.host, event);
    } else {
      value.handleEvent(event);
    }
  }

  protected override disconnected(): void {
    const { eventData, element } = this
    for (const key in eventData) {
      const name = key.slice(1);
      const value = eventData[key] as EventListenerWithOptions;
      element.removeEventListener(name, this, value);
    }
  }

  protected override reconnected(): void {
    const { eventData, element } = this
    for (const key in eventData) {
      const name = key.slice(1);
      const value = eventData[key] as EventListenerWithOptions;
      element.addEventListener(name, this, value);
    }
  }
}

export const spreadEvents = directive(SpreadEventsDirective);

export class SpreadDirective extends SpreadEventsDirective {
  override apply(data: { [key: string]: unknown; }): void {
    if (!data) { return; }
    const { prevData, element } = this
    for (const key in data) {
      const value = data[key];
      if (value === prevData[key]) {
        continue;
      }
      const name = key.slice(1);
      switch (key[0]) {
        case "@":
          this.eventData[name] = value;
          this.applyEvent(name, value as EventListenerWithOptions);
          break;
        case ".":
          //@ts-ignore
          element[name] = value;
          break;
        case "?":
          if (value) {
            element.setAttribute(name, "");
          } else {
            element.removeAttribute(name)
          }
          break;
        default:
          if (value != null) {
            element.setAttribute(key, String(value));
          } else {
            element.removeAttribute(key)
          }
          break;
      }
    }
  }

  override groom(data: { [key: string]: unknown; }): void {
    const { prevData, element } = this
    if (!prevData) { return; }
    for (const key in prevData) {
      const name = key.slice(1);
      //@ts-ignore
      if (!data || (!(key in data) && element[name] === prevData[key])) {
        switch (key[0]) {
          case '@':
            this.groomEvent(name,prevData[key] as EventListenerWithOptions);
            break;
          case '.':
            //@ts-ignore
            element[name] = undefined
            break;
          case '?':
            element.removeAttribute(name);
            break;
          default:
            element.removeAttribute(key);
            break;
        }
      }
    }
  }
}

export const spread = directive(SpreadDirective);
